import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "./components/AuthProvider";
import { AutumnProvider, autumnConfig } from "@/integrations/autumn/client";
import { supabase } from "@/integrations/supabase/client";
import { MainLayout } from "./components/MainLayout";
import { LandingLayout } from "./components/LandingLayout";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { FlowRouter } from "./components/FlowRouter";
import { FlowAccessGate } from "./components/FlowAccessGate";
import { SEO } from "@/components/SEO";
import { ScrollToTop } from "@/components/ScrollToTop";
import { AffiliateTracker } from "@/components/AffiliateTracker";
import { CookieConsent } from "@/components/CookieConsent";
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import PaymentFlow from "./pages/PaymentFlow";
import SignupFlow from "./pages/SignupFlow";
import NewsletterFlow from "./pages/NewsletterFlow";
import ReferralFlow from "./pages/ReferralFlow";
import NotFound from "./pages/NotFound";
import ThankYou from "./pages/ThankYou";
import Terms from "./pages/Terms";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import ValidateStartup from "./pages/ValidateStartup";
import PreLaunchGuide from "@/pages/PreLaunchGuide";
import Features from "@/pages/Features";
import CaseStudies from "@/pages/CaseStudies";
import { BuyButtonFlow } from "@/pages/BuyButtonFlow";
import Guides from "@/pages/Guides";
import Subscription from "@/pages/Subscription";
import RequireSubscription from "@/components/RequireSubscription";
import Demos from "./pages/Demos";
import Logout from "./pages/Logout";
import { AffiliateProgram } from "@/pages/Affiliate/AffiliateProgram";
import TermsOfUse from "./pages/TermsOfUse";
import ResetPassword from "./pages/ResetPassword";

// Lazy load heavy components for better initial page load
const Analytics = lazy(() => import("./pages/Analytics"));
const EditFlow = lazy(() => import("./pages/EditFlow"));
const CreateFlow = lazy(() => import("./pages/CreateFlow"));
const Account = lazy(() => import("./pages/Account"));

// Loading fallback for lazy-loaded components
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-[400px]">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
  </div>
);

const queryClient = new QueryClient();

// Helper components to wrap pages with layouts
const IndexPage = () => (
  <LandingLayout>
    <Index />
  </LandingLayout>
);

const DashboardPage = () => (
  <ProtectedRoute>
    <MainLayout>
      <Suspense fallback={<PageLoader />}>
        <Analytics />
      </Suspense>
    </MainLayout>
  </ProtectedRoute>
);

const CreateFlowPage = () => (
  <ProtectedRoute>
    <MainLayout>
      <Suspense fallback={<PageLoader />}>
        <CreateFlow />
      </Suspense>
    </MainLayout>
  </ProtectedRoute>
);

const EditFlowPage = () => (
  <ProtectedRoute>
    <RequireSubscription>
      <MainLayout>
        <Suspense fallback={<PageLoader />}>
          <EditFlow />
        </Suspense>
      </MainLayout>
    </RequireSubscription>
  </ProtectedRoute>
);

const SubscriptionPage = () => (
  <LandingLayout>
    <Subscription />
  </LandingLayout>
);

// Analytics detail view — accessible for all authenticated users (view-only for grace/expired)
const ProtectedAnalyticsPage = () => (
  <ProtectedRoute>
    <MainLayout>
      <Suspense fallback={<PageLoader />}>
        <Analytics />
      </Suspense>
    </MainLayout>
  </ProtectedRoute>
);

const TermsPage = () => (
  <LandingLayout>
    <Terms />
  </LandingLayout>
);

const PrivacyPolicyPage = () => (
  <LandingLayout>
    <PrivacyPolicy />
  </LandingLayout>
);

const ValidateStartupPage = () => (
  <LandingLayout>
    <ValidateStartup />
  </LandingLayout>
);

const PreLaunchGuidePage = () => (
  <LandingLayout>
    <PreLaunchGuide />
  </LandingLayout>
);

const AffiliateProgramPage = () => (
  <LandingLayout>
    <AffiliateProgram />
  </LandingLayout>
);

const FeaturesPage = () => (
  <LandingLayout>
    <Features />
  </LandingLayout>
);

const CaseStudiesPage = () => (
  <LandingLayout>
    <CaseStudies />
  </LandingLayout>
);

const NotFoundPage = () => (
  <LandingLayout>
    <NotFound />
  </LandingLayout>
);

const GuidesPage = () => (
  <LandingLayout>
    <Guides />
  </LandingLayout>
);

const DemosPage = () => (
  <LandingLayout>
    <Demos />
  </LandingLayout>
);

const TermsOfUsePage = () => (
  <LandingLayout>
    <TermsOfUse />
  </LandingLayout>
);

const AppRoutes = () => {
  return (
    <>
      <SEO />
      <ScrollToTop />
      <AffiliateTracker />
      <Routes>
        <Route path="/" element={<IndexPage />} />
        <Route path="/auth" element={<Auth />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/create" element={<CreateFlowPage />} />
        <Route path="/edit/:flowId" element={<EditFlowPage />} />
        <Route path="/demos" element={<DemosPage />} />
        
        {/* Logout route */}
        <Route path="/logout" element={<Logout />} />
        
        {/* Updated to require subscription */}
        <Route path="/analytics" element={<ProtectedAnalyticsPage />} />
        <Route path="/analytics/:flowId" element={<ProtectedAnalyticsPage />} />
        
        {/* Affiliate Program */}
        <Route path="/affiliate" element={<AffiliateProgramPage />} />

        {/* Subscription page */}
        <Route path="/subscription" element={<SubscriptionPage />} />
        
        {/* Account page */}
        <Route path="/account" element={
          <ProtectedRoute>
            <MainLayout>
              <Suspense fallback={<PageLoader />}>
                <Account />
              </Suspense>
            </MainLayout>
          </ProtectedRoute>
        } />
        
        {/* Legal pages */}
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/validate-startup" element={<ValidateStartupPage />} />
        <Route path="/pre-launch-guide" element={<PreLaunchGuidePage />} />
        <Route path="/features" element={<FeaturesPage />} />
        <Route path="/case-studies" element={<CaseStudiesPage />} />
        <Route path="/terms-of-use" element={<TermsOfUsePage />} />
        
        {/* Flow pages — gated by owner subscription state */}
        <Route path="/flow/:flowId" element={<FlowRouter />} />
        <Route path="/payment/:flowId" element={<FlowAccessGate><PaymentFlow /></FlowAccessGate>} />
        <Route path="/pricing-table/:flowId" element={<FlowAccessGate><PaymentFlow /></FlowAccessGate>} />
        <Route path="/buy-button/:flowId" element={<FlowAccessGate><BuyButtonFlow /></FlowAccessGate>} />
        <Route path="/signup/:flowId" element={<FlowAccessGate><SignupFlow /></FlowAccessGate>} />
        <Route path="/newsletter/:flowId" element={<FlowAccessGate><NewsletterFlow /></FlowAccessGate>} />
        <Route path="/referral/:flowId" element={<FlowAccessGate><ReferralFlow /></FlowAccessGate>} />
        <Route path="/thank-you/:flowId" element={<FlowAccessGate><ThankYou /></FlowAccessGate>} />
        
        <Route path="/guides" element={<GuidesPage />} />
        <Route path="/how-it-works" element={<Navigate to="/guides" replace />} />
        
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <HelmetProvider>
      <AuthProvider>
        <AutumnProvider
          backendUrl={autumnConfig.backendUrl}
          includeCredentials={false}
          disableAutoFetch={true}
          getBearerToken={async () => {
            try {
              const { data, error } = await supabase.auth.getSession();
              if (error) {
                return null;
              }
              return data.session?.access_token || null;
            } catch (error) {
              return null;
            }
          }}
        >
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <CookieConsent />
            <BrowserRouter>
              <AppRoutes />
            </BrowserRouter>
          </TooltipProvider>
        </AutumnProvider>
      </AuthProvider>
    </HelmetProvider>
  </QueryClientProvider>
);

export default App;
